const API = process.env.REACT_APP_API_URL || '';

function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchProducts(token) {
  const res = await fetch(`${API}/products`, {
    headers: { ...authHeader(token) }
  });
  if (!res.ok) throw new Error('Failed to fetch products');
  return res.json();
}

// Blogs
export async function fetchBlogs(token) {
  const res = await fetch(`${API}/blogs`, { headers: { ...authHeader(token) } });
  if (!res.ok) throw new Error('Failed to fetch blogs');
  return res.json();
}

export async function createBlog(data, token) {
  const res = await fetch(`${API}/blogs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Create blog failed');
  return res.json();
}

export async function updateBlog(blogId, data, token) {
  const res = await fetch(`${API}/blogs/${blogId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Update blog failed');
  return res.json();
}

export async function deleteBlog(blogId, token) {
  const res = await fetch(`${API}/blogs/${blogId}`, {
    method: 'DELETE',
    headers: { ...authHeader(token) },
  });
  if (!res.ok) throw new Error('Delete blog failed');
  return res.json();
}

// Users
export async function fetchUsers(token) {
  const res = await fetch(`${API}/users`, { headers: { ...authHeader(token) } });
  if (!res.ok) throw new Error('Failed to fetch users');
  return res.json();
}

export async function updateUser(userId, data, token) {
  const res = await fetch(`${API}/users/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Update user failed');
  return res.json();
}

export async function deleteUser(userId, token) {
  const res = await fetch(`${API}/users/${userId}`, {
    method: 'DELETE',
    headers: { ...authHeader(token) },
  });
  if (!res.ok) throw new Error('Delete user failed');
  return res.json();
}

export async function createProduct(data, token) {
  const res = await fetch(`${API}/products`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Create failed');
  return res.json();
}

export async function updateProduct(productId, data, token) {
  const res = await fetch(`${API}/products/${productId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Update failed');
  return res.json();
}

export async function uploadProductImage(productId, file, token) {
  const fd = new FormData();
  fd.append('image', file);
  const res = await fetch(`${API}/products/${productId}/images`, {
    method: 'POST',
    headers: { ...authHeader(token) },
    body: fd,
  });
  if (!res.ok) throw new Error('Image upload failed');
  return res.json();
}

export async function uploadBlogImage(blogId, file, token) {
  const fd = new FormData();
  fd.append('image', file);
  const res = await fetch(`${API}/blogs/${blogId}/images`, {
    method: 'POST',
    headers: { ...authHeader(token) },
    body: fd,
  });
  if (!res.ok) throw new Error('Blog image upload failed');
  return res.json();
}

export async function deleteProduct(productId, token) {
  const res = await fetch(`${API}/products/${productId}`, {
    method: 'DELETE',
    headers: { ...authHeader(token) },
  });
  if (!res.ok) throw new Error('Delete failed');
  return res.json();
}

export async function deleteProductImage(productId, imageUrl, token) {
  const res = await fetch(`${API}/products/${productId}/images`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify({ imageUrl }),
  });
  if (!res.ok) throw new Error('Delete image failed');
  return res.json();
}

export async function reorderProductImages(productId, images, token) {
  const res = await fetch(`${API}/products/${productId}/images/reorder`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify({ images }),
  });
  if (!res.ok) throw new Error('Reorder images failed');
  return res.json();
}

export async function reorderProducts(productOrders, token) {
  const res = await fetch(`${API}/products/reorder/all`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify({ productOrders }),
  });
  if (!res.ok) throw new Error('Reorder products failed');
  return res.json();
}

export async function reorderBlogs(blogOrders, token) {
  const res = await fetch(`${API}/blogs/reorder/all`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify({ blogOrders }),
  });
  if (!res.ok) throw new Error('Reorder blogs failed');
  return res.json();
}

export async function deleteBlogImage(blogId, imageUrl, token) {
  const res = await fetch(`${API}/blogs/${blogId}/images`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify({ imageUrl }),
  });
  if (!res.ok) throw new Error('Delete blog image failed');
  return res.json();
}

export async function adminLoginWithToken(token) {
  // optional helper to verify token and fetch user; backend lacks a verify endpoint so we rely on stored user
  return { token };
}

// Settings - Dropdown Options
export async function fetchDropdownOptions(token) {
  const res = await fetch(`${API}/settings/dropdown-options`, {
    headers: { ...authHeader(token) }
  });
  if (!res.ok) throw new Error('Failed to fetch dropdown options');
  return res.json();
}

export async function updateDropdownOptions(dropdownOptions, token) {
  const res = await fetch(`${API}/settings/dropdown-options`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify({ dropdownOptions }),
  });
  if (!res.ok) throw new Error('Failed to update dropdown options');
  return res.json();
}

export async function fetchNewsletterSubscribers(token) {
  const res = await fetch(`${API}/settings/newsletter/subscribers`, {
    headers: { ...authHeader(token) }
  });
  if (!res.ok) throw new Error('Failed to fetch newsletter subscribers');
  return res.json();
}

export async function sendNewsletterToAll(payload, token) {
  const res = await fetch(`${API}/settings/newsletter/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let message = 'Failed to send newsletter';
    try {
      const data = await res.json();
      if (data?.message) message = data.message;
    } catch (e) {}
    throw new Error(message);
  }
  return res.json();
}

// PhotoBooth (admin)
export async function fetchPhotoboothPhotos(token) {
  const res = await fetch(`${API}/photobooth`, { headers: { ...authHeader(token) } });
  if (!res.ok) throw new Error('Failed to fetch photobooth photos');
  return res.json();
}

export async function uploadPhotoboothPhoto(file, garmentName, productId, modelName, alt, token) {
  const fd = new FormData();
  fd.append('image', file);
  if (garmentName) fd.append('garmentName', garmentName);
  if (productId) fd.append('product', productId);
  if (modelName) fd.append('modelName', modelName);
  if (alt !== undefined) fd.append('alt', alt);

  const res = await fetch(`${API}/photobooth`, {
    method: 'POST',
    headers: { ...authHeader(token) },
    body: fd,
  });
  if (!res.ok) throw new Error('Photobooth upload failed');
  return res.json();
}

export async function updatePhotoboothPhoto(photoId, data, token) {
  // If data includes a File under `image`, send as FormData so backend can accept file
  let res;
  if (data && data.image instanceof File) {
    const fd = new FormData();
    fd.append('image', data.image);
    if (data.garmentName !== undefined) fd.append('garmentName', data.garmentName);
    if (data.product !== undefined) fd.append('product', data.product);
    if (data.modelName !== undefined) fd.append('modelName', data.modelName);
    if (data.alt !== undefined) fd.append('alt', data.alt);
    if (data.sortOrder !== undefined) fd.append('sortOrder', data.sortOrder);

    res = await fetch(`${API}/photobooth/${photoId}`, {
      method: 'PUT',
      headers: { ...authHeader(token) },
      body: fd,
    });
  } else {
    res = await fetch(`${API}/photobooth/${photoId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeader(token) },
      body: JSON.stringify(data),
    });
  }
  if (!res.ok) throw new Error('Failed to update photobooth photo');
  return res.json();
}

export async function deletePhotoboothPhoto(photoId, token) {
  const res = await fetch(`${API}/photobooth/${photoId}`, {
    method: 'DELETE',
    headers: { ...authHeader(token) },
  });
  if (!res.ok) throw new Error('Failed to delete photobooth photo');
  return res.json();
}

export async function reorderPhotoboothPhotos(photoOrders, token) {
  await Promise.all(
    photoOrders.map(({ id, sortOrder }) =>
      updatePhotoboothPhoto(id, { sortOrder }, token)
    )
  );
  return { message: 'Photobooth order updated' };
}
